import { Check, Palette } from 'lucide-react';
import { motion } from 'motion/react';
import { Popover } from 'radix-ui';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn';
import { useTheme } from './ThemeProvider';
import { SYSTEM_THEMES, THEME_IDS, THEMES, type ThemeChoice, type ThemeId } from './themes';

// Theme picker (§17a). Each card is a live miniature of the app in that theme. Choosing one
// spreads it from the click point as a circle of light (ThemeProvider).

export const THEME_CHOICES: ThemeChoice[] = ['system', ...THEME_IDS];

export function ThemeSwitcher({ align = 'end' }: { align?: 'start' | 'end' }) {
  const { t } = useTranslation();
  const { choice } = useTheme();
  const name = t(choice === 'system' ? 'theme.system.name' : `theme.${choice}.name`);
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <motion.button
          whileHover={{ rotate: -12, scale: 1.06 }}
          whileTap={{ scale: 0.92 }}
          className="grid h-9 w-9 place-items-center rounded-full border border-border bg-surface/70 text-text backdrop-blur transition-colors hover:bg-subtle"
          aria-label={`${t('theme.button')} — ${t('theme.current', { name })}`}
        >
          <Palette size={17} />
        </motion.button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={10}
          className="glass z-50 w-[min(400px,calc(100vw-24px))] rounded-xl p-4 elevated data-[state=open]:animate-in"
        >
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
          >
            <p className="font-display text-md font-bold text-text">{t('theme.title')}</p>
            <p className="mb-3 text-sm text-muted">{t('theme.subtitle')}</p>
            <ThemeGrid compact />
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function ThemeGrid({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { choice, setTheme } = useTheme();

  const pick = (id: ThemeChoice, e: MouseEvent) => setTheme(id, { x: e.clientX, y: e.clientY });

  return (
    <div
      role="radiogroup"
      aria-label={t('theme.title')}
      className={cn(
        'grid gap-2.5',
        compact ? 'grid-cols-2' : 'grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4',
      )}
    >
      {THEME_CHOICES.map((id, i) => {
        const selected = choice === id;
        const label = t(id === 'system' ? 'theme.system.name' : `theme.${id}.name`);
        const mood = t(id === 'system' ? 'theme.system.mood' : `theme.${id}.mood`);
        return (
          <motion.button
            key={id}
            role="radio"
            aria-checked={selected}
            title={mood}
            onClick={(e) => pick(id, e)}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.035 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            className={cn(
              'group relative overflow-hidden rounded-lg border text-left transition-colors',
              selected ? 'border-ring' : 'border-border hover:border-muted',
            )}
          >
            <ThemePreview id={id} tall={!compact} />
            <div
              className={cn(
                'flex items-center justify-between gap-2 bg-surface px-3 py-2',
                !compact && 'min-h-[4.25rem]',
              )}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-text">{label}</p>
                {!compact && <p className="line-clamp-2 text-xs text-muted">{mood}</p>}
              </div>
              {selected && (
                <motion.span
                  layoutId={compact ? 'theme-check-compact' : 'theme-check'}
                  className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-on-primary"
                >
                  <Check size={12} strokeWidth={3} />
                </motion.span>
              )}
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}

/** A tiny rendering of the app shell in a theme's own colours (independent of the active one). */
export function ThemePreview({ id, tall = false }: { id: ThemeChoice; tall?: boolean }) {
  if (id === 'system') {
    return (
      <div className={cn('flex', tall ? 'h-32' : 'h-16')}>
        <div className="w-1/2 overflow-hidden">
          <Mini theme={SYSTEM_THEMES.light} tall={tall} />
        </div>
        <div className="w-1/2 overflow-hidden">
          <Mini theme={SYSTEM_THEMES.dark} tall={tall} />
        </div>
      </div>
    );
  }
  return <Mini theme={id} tall={tall} />;
}

function Mini({ theme, tall }: { theme: ThemeId; tall: boolean }) {
  const c = THEMES[theme];
  return (
    <div
      className={cn('relative flex gap-1.5 overflow-hidden p-2', tall ? 'h-32' : 'h-16')}
      style={{ background: c.bg }}
    >
      <div
        className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-60 blur-xl"
        style={{ background: `radial-gradient(circle, ${c.glow[0]}, ${c.glow[1]})` }}
      />
      <div
        className="w-1/4 rounded-sm"
        style={{ background: c.surface, border: `1px solid ${c.border}` }}
      >
        <div className="m-1 h-1.5 rounded-full" style={{ background: c.primary }} />
        <div className="m-1 h-1 w-2/3 rounded-full" style={{ background: c.muted, opacity: 0.5 }} />
        <div className="m-1 h-1 w-1/2 rounded-full" style={{ background: c.muted, opacity: 0.5 }} />
      </div>
      <div className="relative flex flex-1 flex-col gap-1">
        <div className="h-1.5 w-1/2 rounded-full" style={{ background: c.text }} />
        <div className="flex flex-1 gap-1">
          <div
            className="flex-1 rounded-sm"
            style={{ background: c.surface, border: `1px solid ${c.border}` }}
          >
            <div className="m-1 h-2 w-3/4 rounded-sm" style={{ background: c.primary }} />
          </div>
          <div
            className="flex-1 rounded-sm"
            style={{ background: c.surface, border: `1px solid ${c.border}` }}
          >
            <div className="m-1 h-2 w-2 rounded-full" style={{ background: c.accent }} />
          </div>
        </div>
      </div>
    </div>
  );
}
